import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getCompanyResponses,
  getAllResponsesWithCount,
  getDataInCsv,
} from "../../utils/api/submissions";
import { selectUser, logout } from "../../redux/users/usersSlice";
import Papa from "papaparse";
import { getCompanyById } from "../../utils/api/company";

import ShowCompanyForm from "../../components/company/ShowCompanyForm";
import CompanyResponseWithSuccess from "../../components/modals/supervisor/supervisor-response-success/CompanyResponseWithSuccess";
import CompanyResponseWithPending from "../../components/modals/supervisor/supervisor-response-pending/CompanyResponseWithPending";
import CompanyResponseWithInProgress from "../../components/modals/supervisor/supervisor-response-inprogress/CompanyResponseWithInProgress";
import Search from "../../components/custom-components/Search/Search";
import Loader from "../../components/custom-components/Loader/Loader";
import UploadFileDialogUser from "../../components/company/FileUploadDialogUser";
import CustomModal from "../../components/custom-components/custom-modal/CustomModal";

function Supervisor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const [fullData, setFullData] = useState(null);
  const [timePeriod, setTimePeriod] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    success: 0,
    inProgress: 0,
    pending: 0,
  });
  const openModal = (response) => {
    setSelectedResponse(response);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedResponse(null);
  };

  const handleTimePeriodToggle = (period) => {
    // Toggle between selecting and de-selecting a time period
    const newPeriod = timePeriod === period ? null : period;
    console.log(newPeriod);
    setTimePeriod(newPeriod);
  };

  const downloadCsv = async () => {
    try {
      const res = await getCompanyResponses(user.company);
      const _fullData = res.data.data;
      const allFieldNames = new Set();
      _fullData.forEach((item) => {
        item.response.forEach((field) => {
          allFieldNames.add(field.fieldName);
        });
      });
      const fieldNamesArray = Array.from(allFieldNames);

      const csvData = _fullData.map((item) => {
        const row = {
          "Employee Name": item.employee?.name || "",
          "Employee Email": item.employee?.email || "",
          "Form Name": item.company?.form?.name || "",
          Status: item.status || "",
        };

        fieldNamesArray.forEach((fieldName) => {
          const field = item.response.find((f) => f.fieldName === fieldName);
          row[fieldName] = field ? field.fieldValue : "";
        });

        return row;
      });

      const headers = [
        "Employee Name",
        "Employee Email",
        "Form Name",
        "Status",
        ...fieldNamesArray,
      ];

      const csv = Papa.unparse({
        fields: headers,
        data: csvData,
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", "form_responses.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV: ", error);
    }
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const formDataResponse = await getCompanyById(user.company);
        setCompanyData(formDataResponse.data.data);
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    if (user?.company) {
      fetchCompanyData();
    }
  }, [user?.company]);

  useEffect(() => {
    const getCompaniesData = async () => {
      try {
        setFullLoading(true);
        const result = await getAllResponsesWithCount(
          user?.company,
          timePeriod,
          searchTerm
        );
        setFullData([]);
        const _counts = result?.data.count;
        setStatusCounts({
          success: _counts.find(x => x._id === 'success')?.count ?? 0,
          inProgress: _counts.find(x => x._id === 'in-progress')?.count ?? 0,
          pending: _counts.find(x => x._id === 'pending')?.count ?? 0,
        });
      } catch (err) {
        setFullLoading(false);
        setFullData([]);
        setStatusCounts({ success: 0, inProgress: 0, pending: 0 });
        console.log(err);
      } finally {
        setFullLoading(false);
      }
    };

    if (user?.company) {
      getCompaniesData();
    }
  }, [user?.company, searchTerm, timePeriod]);

  return (
    <div className="flex flex-col h-screen">
      {/* <Response /> */}
      <header className="bg-gray-800 text-white p-4 flex flex-row justify-between items-center">
        <h1 className="text-xl font-semibold">Form Responses</h1>
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <Search
            placeholder="Search by name, email"
            handleSearchInputChange={(e) => setSearchTerm(e.target.value)}
            searchQuery={searchTerm}
            bgDark={true}
          />

          <div className="flex gap-2">
            <button
              onClick={() => {
                dispatch(logout());
                navigate("/users/sign-in");
              }}
              className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-lg"
            >
              Logout
            </button>
            <button
              onClick={downloadCsv}
              className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-green-600 rounded-lg"
            >
              {fullLoading ? <Loader size={"w-5 h-5"} /> : "Export CSV"}
            </button>
            <button
              onClick={() => {
                setShowFileUploadDialog(true);
              }}
              className="px-2 py-1 text-white bg-blue-500 rounded hover:bg-blue-600"
            >
              Upload File
            </button>
          </div>
        </div>
      </header>

      <main className="flex flex-1 overflow-hidden">
        <div className="w-4/5 px-2 overflow-y-auto">
          <div className="flex flex-wrap items-center justify-center gap-4 py-2 rounded-lg bg-slate-200 sticky top-0 z-10">
            <p className="w-full px-2 mx-4 text-lg font-semibold sm:w-auto sherink-0">
              Filter the data:
            </p>
            <button
              onClick={() => {
                handleTimePeriodToggle("day");
              }}
              className={`px-4 py-2 shrink-0 rounded-lg font-semibold border border-blue-600 ${timePeriod === "day" ? "bg-blue-600 text-white" : "bg-gray-200"
                }`}
            >
              Last Day
            </button>
            <button
              onClick={() => {
                handleTimePeriodToggle("week");
              }}
              className={`px-4 py-2 shrink-0 rounded-lg font-semibold border border-blue-600 ${timePeriod === "week" ? "bg-blue-600 text-white" : "bg-gray-200"
                }`}
            >
              Last Week
            </button>
            <button
              onClick={() => {
                handleTimePeriodToggle("month");
              }}
              className={`px-4 py-2 shrink-0 rounded-lg font-semibold border border-blue-600 ${timePeriod === "month"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200"
                }`}
            >
              Last Month
            </button>
            <button
              onClick={() => {
                handleTimePeriodToggle("3months");
              }}
              className={`px-4 py-2 shrink-0 rounded-lg font-semibold border border-blue-600 ${timePeriod === "3months"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200"
                }`}
            >
              Last 3 Months
            </button>
            <button
              onClick={() => {
                setTimePeriod(null);
              }}
              className={`px-4 py-2 shrink-0 rounded-lg font-semibold border border-blue-600 ${timePeriod === "" ? "bg-blue-600 text-white" : "bg-gray-200"
                }`}
            >
              Reset
            </button>
          </div>
          <div className="w-[95%] m-auto">
            {user?.company && (
              <div className=" bg-white rounded-lg py-2 shadow-lg">
                <>
                  <h1 className=" text-center p-2 my-2 text-xl font-semibold text-white bg-yellow-500 rounded-xl">
                    Pending Responses
                  </h1>
                  <CompanyResponseWithPending
                    companyId={user?.company}
                    timePeriod={timePeriod}
                    searchTerm={searchTerm}
                  />
                </>
              </div>
            )}
            {user?.company && (
              <div className=" bg-white rounded-lg py-2 shadow-lg">
                <>
                  <h1 className="text-center p-2 my-2 text-xl font-semibold text-white bg-blue-500 rounded-xl">
                    In-Progress Responses
                  </h1>
                  <CompanyResponseWithInProgress
                    companyId={user?.company}
                    timePeriod={timePeriod}
                    searchTerm={searchTerm}
                  />
                </>
              </div>
            )}
            {user?.company && (
              <div className=" bg-white rounded-lg py-2 shadow-lg">
                <>
                  <h1 className="text-center p-2 my-2 text-xl font-semibold text-white bg-green-600 rounded-xl">
                    Success Responses
                  </h1>
                  <CompanyResponseWithSuccess
                    companyId={user?.company}
                    timePeriod={timePeriod}
                    searchTerm={searchTerm}
                  />
                </>
              </div>
            )}
          </div>
        </div>

        <div className="w-1/5 bg-gray-300 p-4 flex flex-col justify-center items-center space-y-4 text-white">
          <h3 className="text-lg font-bold text-black text-center">
            Form Details
          </h3>

          <div className="w-full p-4 text-center bg-yellow-500 rounded-lg">
            <h3 className="text-lg font-bold">Total Pending </h3>
            <p className="text-xl">
              {fullLoading ? (
                <span className="">
                  <Loader size={"w-5 h-5"} />
                </span>
              ) : (
                statusCounts?.pending
              )}
            </p>
          </div>
          <div className="w-full p-4 text-center bg-blue-500 rounded-lg">
            <h3 className="text-lg font-bold">Total In-Progress </h3>
            <p className="text-xl">
              {fullLoading ? (
                <span className="">
                  <Loader size={"w-5 h-5"} />
                </span>
              ) : (
                statusCounts?.inProgress
              )}
            </p>
          </div>
          <div className="w-full p-4 text-center bg-green-600 rounded-lg">
            <h3 className="text-lg font-bold">Total Success </h3>
            <p className="text-xl">
              {fullLoading ? (
                <span className="">
                  <Loader size={"w-5 h-5"} />
                </span>
              ) : (
                statusCounts?.success
              )}
            </p>
          </div>
          <div className="w-full p-4 text-center bg-purple-700 rounded-lg">
            <h3 className="text-lg font-bold">Total Data Used</h3>
            {companyData && (
              <p className="text-xl">{companyData?.usedData + " GB"}</p>
            )}
          </div>
          <div className="w-full p-4 text-center bg-gray-600 rounded-lg">
            <button onClick={() => setShowFormModal(true)}>Company Form</button>
          </div>
        </div>
      </main>

      {showFormModal && companyData && (
        <div className="fixed inset-0 z-10 flex items-center justify-center backdrop-blur-md bg-black/10 ">
          <div className="relative  bg-white rounded-md p-4 shadow-lg overflow-y-auto h-[70%] sm:w-[50%] w-[90%]">
            <h2 className="mb-4 text-xl font-semibold">
              {companyData.name} - Form
            </h2>
            <ShowCompanyForm company={companyData} />
            <button
              className="px-4 py-2 mt-4 text-white bg-red-600 rounded hover:bg-red-500"
              onClick={() => setShowFormModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {isModalOpen && selectedResponse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-1/3 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="mb-4 text-xl font-bold">Employee Details</h2>
            <div className="space-y-2">
              <p>
                <strong>Name:</strong> {selectedResponse.employeeId.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedResponse.employeeId.email}
              </p>
              {selectedResponse.employeeId.phoneNumber && (
                <p>
                  <strong>Phone Number:</strong>{" "}
                  {selectedResponse.employeeId.phoneNumber}
                </p>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 font-semibold text-white bg-gray-600 rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showFileUploadDialog && companyData && (
        <CustomModal isOpen={showFileUploadDialog} closeModal={() => setShowFileUploadDialog(false)}>
          <UploadFileDialogUser
            confirm={(file) => {
              console.log("File uploaded:", file);
              setShowFileUploadDialog(false);
            }}
            item={companyData}
            closePopup={setShowFileUploadDialog}
          />
        </CustomModal>
      )}
    </div>
  );
}

export default Supervisor;
